import React, { useState, useEffect, useCallback } from "react";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import {
  db,
  doc,
  updateDoc,
  addDoc,
  collection,
  getDoc,
} from "../../../../firebaseConfig";
import {
  calculateDayType,
  addBusinessDays,
} from "../../../../Utils/dayTypeCalculator";

import { useAtom } from "jotai";
import {
  themeAtom,
  isAdminAtom,
  jobTypesAtom,
  activeMultiDayIdAtom,
} from "../../../../-Atoms";

const JobTypeContainer = ({
  jobData,
  clientData,
  setHasUnsavedChanges,
  allMultipleDayJobs = [],
}) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [jobTypes] = useAtom(jobTypesAtom);
  const [, setActiveMultiDayId] = useAtom(activeMultiDayIdAtom);
  const [jobType, setJobType] = useState(jobData.jobTypeFull);
  const [isMultiDay, setIsMultiDay] = useState(false);
  const [isAddingDay, setIsAddingDay] = useState(false);
  const [multiDayMenuAnchor, setMultiDayMenuAnchor] = useState(null);

  useEffect(() => {
    setJobType(jobData.jobTypeFull);

    const hasMultipleDays =
      jobData.isMultiDay ||
      (jobData.multiDayChildren && jobData.multiDayChildren.length > 0) ||
      jobData.parentJobId;
    setIsMultiDay(hasMultipleDays);
  }, [jobData]);

  // Auto-calculate and update dayType when job type or duration changes
  useEffect(() => {
    if (!isAdmin || !jobData.id) return;

    const result = calculateDayType(
      jobData.jobTypeFull,
      jobData.bookedStart,
      jobData.bookedFinish
    );

    if (!result) {
      if (jobData.dayType || jobData.creativeDayType) {
        updateDoc(doc(db, "JobData", jobData.id), {
          dayType: "",
          creativeDayType: "",
        }).catch((error) => {
          console.error("Error clearing day type:", error);
        });
      }
      return;
    }

    const needsUpdate =
      (result.dayType && result.dayType !== jobData.dayType) ||
      (result.creativeDayType &&
        result.creativeDayType !== jobData.creativeDayType) ||
      (!result.dayType && jobData.dayType) ||
      (!result.creativeDayType && jobData.creativeDayType);

    if (needsUpdate) {
      updateDoc(doc(db, "JobData", jobData.id), {
        dayType: result.dayType || "",
        creativeDayType: result.creativeDayType || "",
      }).catch((error) => {
        console.error("Error auto-updating day type:", error);
      });
    }
  }, [
    jobData.jobTypeFull,
    jobData.bookedStart,
    jobData.bookedFinish,
    jobData.id,
    jobData.dayType,
    jobData.creativeDayType,
    isAdmin,
  ]);

  // Get suffix pattern: Life/Creative use D1,D2..., Product uses A,B,C...
  const getSuffixPattern = () => {
    const jobTypeShort = jobData.jobTypeShort || "";
    if (jobTypeShort.includes("L") || jobTypeShort.includes("C")) {
      return "D";
    }
    return "A";
  };

  // Calculate next shoot date, skipping weekends (Friday->Monday)
  const getNextDate = (lastDate) => {
    const next = new Date(lastDate);
    const dayOfWeek = lastDate.getDay();
    if (dayOfWeek === 5) {
      next.setDate(next.getDate() + 3);
    } else {
      next.setDate(next.getDate() + 1);
    }
    return next;
  };

  const handleAddDay = async () => {
    if (!isAdmin || isAddingDay) return;
    setIsAddingDay(true);

    try {
      const parentId = jobData.parentJobId || jobData.id;
      const existingJobs = allMultipleDayJobs.length > 0 ? allMultipleDayJobs : [jobData];

      if (existingJobs.length === 0) {
        console.error("No existing jobs found to clone from.");
        setIsAddingDay(false);
        return;
      }

      // Fetch fresh data to prevent race conditions
      const docSnapshots = await Promise.all(
        existingJobs.map((job) => getDoc(doc(db, "JobData", job.id)))
      );

      const freshJobs = docSnapshots.map((snapshot) => ({
        id: snapshot.id,
        ...snapshot.data(),
      }));

      const sortedFreshJobs = freshJobs.sort((a, b) =>
        (a.multiDayOrder || 1) - (b.multiDayOrder || 1)
      );

      const lastJob = sortedFreshJobs[sortedFreshJobs.length - 1];

      const isFirstTimeAdding = !jobData.parentJobId && !jobData.isMultiDay;
      const nextOrder = isFirstTimeAdding ? 2 : (lastJob.multiDayOrder || 1) + 1;

      const pattern = getSuffixPattern();
      const nextSuffix = pattern === "D"
        ? `D${nextOrder}`
        : String.fromCharCode(64 + nextOrder);

      const lastStart = lastJob.bookedStart?.toDate
        ? lastJob.bookedStart.toDate()
        : new Date(lastJob.bookedStart);
      const lastEnd = lastJob.bookedFinish?.toDate
        ? lastJob.bookedFinish.toDate()
        : new Date(lastJob.bookedFinish);

      const nextStart = getNextDate(lastStart);
      const nextEnd = getNextDate(lastEnd);

      const clientTurnaround = parseInt(clientData?.turnAround, 10) || 5;
      const dueDate = addBusinessDays(nextStart, clientTurnaround);

      const jtsRaw = jobType?.match(/^.*\((-.*)\)/);
      const jts = jtsRaw ? jtsRaw[1] : "";

      const newJobData = {
        ...lastJob,
        multipleDays: nextSuffix,
        multiDaySuffix: nextSuffix,
        multiDayOrder: nextOrder,
        isMultiDay: true,
        parentJobId: parentId,
        multiDayChildren: [],
        bookedStart: nextStart.toISOString(),
        bookedFinish: nextEnd.toISOString(),
        start: nextStart.toISOString(),
        end: nextEnd.toISOString(),
        shootDate: nextStart.toISOString(),
        dueDate: dueDate.toISOString(),
        jobName: `${lastJob.jobNumber}-${lastJob.client}-${nextSuffix}${jts}`,
      };

      delete newJobData.id;

      const newJobRef = await addDoc(collection(db, "JobData"), newJobData);

      if (isFirstTimeAdding) {
        const parentSuffix = pattern === "D" ? "D1" : "A";

        await updateDoc(doc(db, "JobData", jobData.id), {
          isMultiDay: true,
          multiDayChildren: [newJobRef.id],
          multiDaySuffix: parentSuffix,
          multipleDays: parentSuffix,
          multiDayOrder: 1,
          parentJobId: null,
          jobName: `${lastJob.jobNumber}-${lastJob.client}-${parentSuffix}${jts}`,
          dueDate: dueDate.toISOString(),
        });
      } else {
        const parentDoc = doc(db, "JobData", parentId);
        const currentChildren = freshJobs
          .filter((j) => j.id !== parentId)
          .map((j) => j.id);

        await updateDoc(parentDoc, {
          multiDayChildren: [...currentChildren, newJobRef.id],
        });

        await Promise.all(freshJobs.map(async (job) => {
          await updateDoc(doc(db, "JobData", job.id), {
            dueDate: dueDate.toISOString(),
          });
        }));
      }

      setActiveMultiDayId(newJobRef.id);
    } catch (error) {
      console.error("Error adding day:", error);
      alert("Failed to add day. Please try again.");
    } finally {
      setIsAddingDay(false);
    }
  };

  // D0 = Pre-production day before actual shoot days
  const supportsD0 = () => getSuffixPattern() === "D";
  const hasD0 = () => allMultipleDayJobs.some(
    (job) => job.multiDaySuffix === "D0" || job.multipleDays === "D0"
  );

  // Shift all days down by 1 (D1→D0, D2→D1, etc.) to add pre-prod day
  const handleMakeD0 = async () => {
    if (!isAdmin || isAddingDay) return;
    setIsAddingDay(true);

    try {
      const jtsRaw = jobType?.match(/^.*\((-.*)\)/);
      const jts = jtsRaw ? jtsRaw[1] : "";

      await Promise.all(allMultipleDayJobs.map(async (job) => {
        const currentOrder = job.multiDayOrder || 1;
        const newOrder = currentOrder - 1;
        const newSuffix = `D${newOrder}`;

        await updateDoc(doc(db, "JobData", job.id), {
          multiDaySuffix: newSuffix,
          multipleDays: newSuffix,
          multiDayOrder: newOrder,
          jobName: `${job.jobNumber}-${job.client}-${newSuffix}${jts}`,
        });
      }));
    } catch (error) {
      console.error("Error making D0:", error);
      alert("Failed to make D0. Please try again.");
    } finally {
      setIsAddingDay(false);
    }
  };

  // Shift all days up by 1 (D0→D1, D1→D2, etc.) to remove pre-prod day
  const handleRemoveD0 = async () => {
    if (!isAdmin || isAddingDay) return;
    setIsAddingDay(true);

    try {
      const jtsRaw = jobType?.match(/^.*\((-.*)\)/);
      const jts = jtsRaw ? jtsRaw[1] : "";

      await Promise.all(allMultipleDayJobs.map(async (job) => {
        const currentOrder = job.multiDayOrder || 0;
        const newOrder = currentOrder + 1;
        const newSuffix = `D${newOrder}`;

        await updateDoc(doc(db, "JobData", job.id), {
          multiDaySuffix: newSuffix,
          multipleDays: newSuffix,
          multiDayOrder: newOrder,
          jobName: `${job.jobNumber}-${job.client}-${newSuffix}${jts}`,
        });
      }));
    } catch (error) {
      console.error("Error removing D0:", error);
      alert("Failed to remove D0. Please try again.");
    } finally {
      setIsAddingDay(false);
    }
  };

  const updateJobName = useCallback(
    (jobNumberChange, clientChange, multipleDaysChange, jobTypeFullChange) => {
      try {
        const jtsRaw = jobTypeFullChange?.match(/^.*\((-.*)\)/);
        let jts = "";
        if (jtsRaw) {
          jts = jtsRaw[1];
        }

        const jn = `${jobNumberChange}-${clientChange}${
          multipleDaysChange ? `-${multipleDaysChange}` : ""
        }${jts}`;

        updateDoc(doc(db, "JobData", jobData.id), { jobName: jn || null });
      } catch (e) {
        console.error("Error updating job name:", e);
      }
    },
    [jobData]
  );

  const handleJobTypeChange = (e) => {
    if (!isAdmin) return;
    const selectedValue = e.target.value;
    setJobType(selectedValue);

    let jobTypeShort = "";
    try {
      const match = selectedValue.match(/^.*\((-.*)\)/);
      if (match && match[1]) {
        jobTypeShort = match[1];
      } else {
        const selectedJobType = jobTypes.find(
          (jt) =>
            typeof jt === "object" && jt !== null && jt.name === selectedValue
        );
        if (selectedJobType && selectedJobType.nameShort) {
          jobTypeShort = selectedJobType.nameShort;
        }
      }
    } catch (error) {
      console.error("Error extracting jobTypeShort:", error);
    }

    if (jobData.jobNumber && jobData.client) {
      updateJobName(
        jobData.jobNumber,
        jobData.client,
        jobData.multipleDays,
        selectedValue
      );
    }

    updateDoc(doc(db, "JobData", jobData.id), {
      jobTypeFull: selectedValue,
      jobTypeShort: jobTypeShort,
    }).catch((error) => {
      console.error("Error updating job type:", error);
    });

    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  };

  const handleJobTypeFocus = () => {
    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  };

  const handleJobTypeBlur = () => {
    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  };

  return (
    <FormControl
      fullWidth
      sx={{
        marginTop: 2,
        position: "relative",
      }}
      variant="filled"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "flex-start",
            gap: 2,
            width: "100%",
          }}
        >
          <Box sx={{ flex: 1, width: "100%" }}>
            <FormControl fullWidth variant="filled">
              <InputLabel id="job-type">Job Type</InputLabel>
              <Select
                disableUnderline
                labelId="job-type-select"
                value={jobType || ""}
                label="Job Type"
                error={jobType ? false : true}
                onChange={handleJobTypeChange}
                onFocus={handleJobTypeFocus}
                onBlur={handleJobTypeBlur}
                sx={{
                  borderRadius: "8px",
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(45,45,45,0.7)"
                      : "rgba(250,250,250,0.9)",
                  boxShadow:
                    theme === "dark"
                      ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                      : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                  "&:hover": {
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(55,55,55,0.8)"
                        : "rgba(255,255,255,1)",
                    boxShadow:
                      theme === "dark"
                        ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                        : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                  },
                }}
              >
                {jobTypes.map((jobType) => {
                  if (typeof jobType === "object" && jobType !== null) {
                    return (
                      <MenuItem
                        key={jobType.id || jobType.name}
                        value={`${jobType.name} (-${jobType.nameShort})`}
                      >
                        {jobType.name} (-{jobType.nameShort})
                      </MenuItem>
                    );
                  } else {
                    return (
                      <MenuItem key={jobType} value={jobType}>
                        {jobType}
                      </MenuItem>
                    );
                  }
                })}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              flex: 1,
              width: "100%",
              display: "flex",
              gap: 1,
              alignItems: "stretch",
            }}
          >
            {/* Show Add Day button - day navigation happens via tabs at top */}
            {isAdmin && (
              <>
                <Button
                  size="medium"
                  startIcon={<AddIcon />}
                  onClick={handleAddDay}
                  disabled={isAddingDay}
                  variant="outlined"
                  sx={{
                    flex: 1,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.95rem",
                    padding: "12px 16px",
                    borderColor:
                      theme === "dark"
                        ? "rgba(255,255,255,0.23)"
                        : "rgba(0,0,0,0.23)",
                    "&:hover": {
                      borderColor: theme === "dark" ? "#90caf9" : "#1976d2",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(144, 202, 249, 0.08)"
                          : "rgba(25, 118, 210, 0.08)",
                    },
                  }}
                >
                  {isAddingDay ? "Adding..." : "Add Day"}
                </Button>

                {/* Options menu for D0 - only for Life/Creative multi-day jobs */}
                {supportsD0() && isMultiDay && (
                  <IconButton
                    onClick={(e) => setMultiDayMenuAnchor(e.currentTarget)}
                    sx={{
                      borderRadius: "8px",
                      border:
                        theme === "dark"
                          ? "1px solid rgba(255,255,255,0.23)"
                          : "1px solid rgba(0,0,0,0.23)",
                      "&:hover": {
                        borderColor: theme === "dark" ? "#90caf9" : "#1976d2",
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(144, 202, 249, 0.08)"
                            : "rgba(25, 118, 210, 0.08)",
                      },
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Multi-day options menu */}
      <Menu
        anchorEl={multiDayMenuAnchor}
        open={Boolean(multiDayMenuAnchor)}
        onClose={() => setMultiDayMenuAnchor(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() => {
            if (hasD0()) {
              handleRemoveD0();
            } else {
              handleMakeD0();
            }
            setMultiDayMenuAnchor(null);
          }}
        >
          {hasD0() ? "Remove D0" : "Add D0"}
        </MenuItem>
      </Menu>
    </FormControl>
  );
};

export default JobTypeContainer;
